import React from 'react';
import { motion } from 'framer-motion';
import { LeafIcon, HeartIcon, TrendingUpIcon, CheckCircle2Icon, UsersIcon, UsersRoundIcon, CalendarCheckIcon } from 'lucide-react';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
import { Link } from 'react-router-dom';
const pillars = [{
  icon: LeafIcon,
  title: 'Sustainability',
  body: 'Paid seats fund the day-to-day delivery of our programs, ensuring quality and consistency for every cohort.',
  bg: 'bg-sage-100'
}, {
  icon: HeartIcon,
  title: 'Social mission',
  body: 'Scholarships open access for families who need support — through transparent criteria and donor-backed seats.',
  bg: 'bg-[#F7EDE9]'
}, {
  icon: TrendingUpIcon,
  title: 'Scale',
  body: 'Institutional grants and partnerships allow us to reach more families across more regions over time.',
  bg: 'bg-sand-100'
}];
const types = [{
  title: 'Partial scholarships',
  body: 'For families with limited income or temporary financial difficulties.'
}, {
  title: 'Full scholarships',
  body: 'Priority support for vulnerable children, including disability and crisis situations.'
}, {
  title: 'Community-sponsored seats',
  body: 'Donor- and partner-funded places with clear criteria and reporting.'
}];
// Pricing section is temporarily hidden. Set to true to restore it.
const SHOW_PRICING_SECTION = false;
export function ScholarshipsPage() {
  return <>
      <SubpageHero eyebrow="Access for every child" title="Scholarships" paragraphs={['Education should be accessible, dignified, and sustainable.', 'Scholarships are supported through donations and institutional partnerships and are allocated transparently based on clear criteria.']} primaryCta={{
      label: 'Apply for a Scholarship',
      to: '/en/waitlist'
    }} secondaryCta={{
      label: 'View Programs',
      to: '/en/children/kids-programs'
    }} image="https://cdn.magicpatterns.com/uploads/8awkEnkAr35x3W2oQBLASf/Scholar-3.jpg" imageAlt="Young Muslim girl studying at a laptop in a bright, book-filled home study" compact masked data-id="element-3377" data-mp="1yklymm68n7if" />

      {/* Hybrid model */}
      <section className="py-16 md:py-24 bg-sage-50/60" data-id="element-3378" data-mp="2s6j9a5em7m47">
        <Container data-id="element-3379" data-mp="2s4yfii04k77e">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-3380" data-mp="2s4gb2y3b6moe">
            Our hybrid model
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-3381" data-mp="30zcqi8dmgvkp">
            We use a simple structure: paid seats create sustainability,
            scholarships deliver social impact, and grants allow scale.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-3382" data-mp="3ay2ac6p2wq3l">
            {pillars.map((p, i) => <motion.div key={p.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className={`rounded-2xl ${p.bg} border border-ink-900/5 p-6 shadow-soft`} data-id="element-3383" data-mp="3l20k7bq3gema" data-mpi={i}>
                <span className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-white border border-forest-700/10 text-forest-700" data-id="element-3384" data-mp="3u0kwnv529s22">
                  <p.icon size={18} strokeWidth={1.6} data-id="element-3385" data-mp="qcsmkll7vd34" />
                </span>
                <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-3386" data-mp="qex0zxgqruhn">
                  {p.title}
                </h3>
                <p className="mt-3 text-ink-700 text-sm leading-relaxed" data-id="element-3387" data-mp="642mfhqy2uaq">
                  {p.body}
                </p>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* Pricing — temporarily hidden, see SHOW_PRICING_SECTION */}
      {SHOW_PRICING_SECTION && <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-3388" data-mp="14q2b4rgk1bu">
        <PatternDecor side="left" y="top" size="lg" data-id="element-3389" data-mp="oukdzj01jjui" />
        <Container data-id="element-3390" data-mp="ov3v4xhva47h">
          <div data-id="element-3391" data-mp="own9ujnvk775">
            <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-3392" data-mp="ttngavomzbys">
              Pricing
            </h2>
            <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-3393" data-mp="g31wn4rcyxzu">
              Transparent, family-friendly pricing — designed so paid seats
              sustain the program and scholarships open access for those who
              need them.
            </p>
          </div>

          {/* Standard tuition */}
          <div className="mt-12" data-id="element-3394" data-mp="1cl6ix1er0o0a">
            <h3 className="font-display text-2xl md:text-3xl text-ink-900" data-id="element-3395" data-mp="1clfzjydwlitb">
              Standard tuition (monthly)
            </h3>
            <p className="mt-3 text-ink-700" data-id="element-3396" data-mp="2sbqlw7emkw2n">
              <span className="font-semibold text-ink-900" data-id="element-3397" data-mp="2saxohx1k3ix6">Included:</span> Core
              Learning / Islamic & Cultural Foundation / Base Enrichment (1
              class)
            </p>

            <div className="mt-6 rounded-2xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-3398" data-mp="322l85brvfvo3">
              <div className="grid grid-cols-[1fr_1.5fr_auto] gap-4 px-6 py-4 bg-sage-50 border-b border-sage-200 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-900" data-id="element-3399" data-mp="2ec7q8e303d78">
                <span data-id="element-3400" data-mp="2oax1vp4b01f1">Age group</span>
                <span data-id="element-3401" data-mp="2o7sn5xt7pna4">Format</span>
                <span data-id="element-3402" data-mp="2ja9av3g2rkkt">Price</span>
              </div>
              {[{
              age: '5–7',
              format: '2 classes / week',
              price: '€90'
            }, {
              age: '8–10',
              format: '2 classes (+1 free optional class) / week',
              price: '€120'
            }, {
              age: '11–14',
              format: '3 classes / week',
              price: '€150'
            }, {
              age: '15–18',
              format: '3 classes / week',
              price: '€180'
            }].map((row, i, arr) => <div key={row.age} className={`grid grid-cols-[1fr_1.5fr_auto] gap-4 px-6 py-5 items-center ${i < arr.length - 1 ? 'border-b border-sage-200' : ''}`} data-id="element-3403" data-mp="3j3pioz40gsuc" data-mpi={i}>
                  <span className="font-display text-lg text-ink-900" data-id="element-3404" data-mp="3szj12z4evgk1">
                    {row.age}
                  </span>
                  <span className="text-ink-700" data-id="element-3405" data-mp="1nzjcz9z1cq5">{row.format}</span>
                  <span className="font-display text-lg text-forest-700 font-semibold" data-id="element-3406" data-mp="30bn4rvir7qns">
                    {row.price}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Enrichment classes */}
          <div className="mt-14" data-id="element-3407" data-mp="1ietbg7srurxk">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-900" data-id="element-3408" data-mp="1ifuv30nusmqw">
              Enrichment classes (optional)
            </h3>

            <div className="mt-5 rounded-2xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-3409" data-mp="18gm50iw3zs9t">
              <div className="grid grid-cols-[1.5fr_1fr] gap-4 px-6 py-4 bg-sage-50 border-b border-sage-200 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-900" data-id="element-3410" data-mp="13hsrwrkqi3a6">
                <span data-id="element-3411" data-mp="uoyxfmwpuxbq">Class</span>
                <span data-id="element-3412" data-mp="uo5vr0cm6y0g">Price</span>
              </div>
              {[{
              name: 'Additional language',
              price: '€25–40 / month'
            }, {
              name: 'Calligraphy',
              price: '€20–30 / month'
            }, {
              name: 'Islamic Arts & Crafts',
              price: '€30–50 / month'
            }].map((row, i, arr) => <div key={row.name} className={`grid grid-cols-[1.5fr_1fr] gap-4 px-6 py-5 items-center ${i < arr.length - 1 ? 'border-b border-sage-200' : ''}`} data-id="element-3413" data-mp="28nzcd3k8djko" data-mpi={i}>
                  <span className="text-ink-900 font-medium" data-id="element-3414" data-mp="1zlq4ymk9e6bi">{row.name}</span>
                  <span className="font-display text-lg text-forest-700 font-semibold" data-id="element-3415" data-mp="1kxd6nixp8bdu">
                    {row.price}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Family packages */}
          <div className="mt-14" data-id="element-3416" data-mp="1bz1jjqw1jvq5">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-900" data-id="element-3417" data-mp="1gwb7f23s7zbv">
              Family packages
            </h3>
            <p className="mt-3 text-ink-700 leading-relaxed" data-id="element-3418" data-mp="120bzpskl4179">
              Bringing more than one child? Committing for the year? We make it
              easier with built-in savings.
            </p>

            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-3419" data-mp="nd3j2c8ib3nw">
              {[{
              icon: UsersIcon,
              bg: 'bg-sage-100',
              border: 'border-forest-700/10',
              eyebrow: 'Multi-child discount',
              value: '−15%',
              label: '2 children enrolled',
              note: 'Applied automatically to your monthly tuition.'
            }, {
              icon: UsersRoundIcon,
              bg: 'bg-[#F7EDE9]',
              border: 'border-[#E0BFB3]/40',
              eyebrow: 'Multi-child discount',
              value: '−25%',
              label: '3 or more children',
              note: 'Our best rate for larger families.'
            }, {
              icon: CalendarCheckIcon,
              bg: 'bg-sand-100',
              border: 'border-sand-300/50',
              eyebrow: 'Annual commitment',
              value: '2 months free',
              label: 'Pay for 10, get 12',
              note: 'Commit for the year and two months are on us.'
            }].map((pkg, i) => <motion.div key={pkg.label} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.08,
              ease: [0.22, 1, 0.36, 1]
            }} className={`relative overflow-hidden rounded-2xl ${pkg.bg} border ${pkg.border} p-6 shadow-soft`} data-id="element-3420" data-mp="rci5bqxf0nuf" data-mpi={i}>
                  <span className="w-12 h-12 inline-flex items-center justify-center rounded-xl bg-white border border-forest-700/10 text-forest-700" data-id="element-3421" data-mp="3qw2jojcdwmln">
                    <pkg.icon size={22} strokeWidth={1.6} data-id="element-3422" data-mp="2bbtjzcs88xvy" />
                  </span>
                  <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700" data-id="element-3423" data-mp="2fc3p6n873k9r">
                    {pkg.eyebrow}
                  </p>
                  <p className="mt-2 font-display text-3xl md:text-4xl text-ink-900 leading-none" data-id="element-3424" data-mp="2k4wrl4g6ygio">
                    {pkg.value}
                  </p>
                  <p className="mt-3 font-display text-lg text-ink-900" data-id="element-3425" data-mp="2tzyio5u11snw">
                    {pkg.label}
                  </p>
                  <p className="mt-2 text-ink-700 text-sm leading-relaxed" data-id="element-3426" data-mp="2yy8fekveh1bm">
                    {pkg.note}
                  </p>
                </motion.div>)}
            </div>

            <p className="mt-5 flex items-center gap-2 text-ink-500 text-sm" data-id="element-3427" data-mp="14o4so1o0g5lx">
              <CheckCircle2Icon size={15} className="text-forest-700 flex-shrink-0" strokeWidth={1.8} data-id="element-3428" data-mp="18oongyl42x4w" />
              Discounts can be combined with scholarships where eligible.
            </p>
          </div>
        </Container>
      </section>}

      {/* Types of support */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-[#EDF2F7]/60" data-id="element-3429" data-mp="2kx66mpjaltk8">
        <PatternDecor side="right" y="bottom" size="md" data-id="element-3430" data-mp="2h0a30l45zcct" />
        <Container data-id="element-3431" data-mp="2uwdsy2808yjh">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-3432" data-mp="2uvuamkeg084x">
            Types of support
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-3433" data-mp="34ni4st2dw3a3">
            No child should be excluded from our programs because of financial
            circumstances.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-3434" data-mp="2zqhyqi5jb0iz">
            {types.map((t, i) => {
            const tones = ['bg-sage-100 border-forest-700/10', 'bg-[#F7EDE9] border-[#E0BFB3]/40', 'bg-sand-100 border-sand-300/40'];
            return <motion.div key={t.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.08
            }} className={`rounded-2xl border p-6 shadow-soft ${tones[i % tones.length]}`} data-id="element-3435" data-mp="14bzbn61hkjz" data-mpi={i}>
                  <h3 className="font-display text-lg text-ink-900" data-id="element-3436" data-mp="34m2k1mhxpwvl">
                    {t.title}
                  </h3>
                  <p className="mt-3 text-ink-700 text-sm leading-relaxed" data-id="element-3437" data-mp="2qmb95umpswlt">
                    {t.body}
                  </p>
                </motion.div>;
          })}
          </div>
        </Container>
      </section>

      {/* How allocation works */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-3438" data-mp="2ilv6sldaf5kj">
        <Container data-id="element-3439" data-mp="2du3qr4rrci8n">
          <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-3440" data-mp="2dtlkrgfampnj">
            <div className="lg:col-span-6" data-id="element-3441" data-mp="2drqynysacrhv">
              <h2 className="font-display text-3xl md:text-4xl text-ink-900 leading-tight" data-id="element-3442" data-mp="29tbd7zburr6k">
                How allocation works
              </h2>
              <ul className="mt-6 space-y-4 text-ink-700" data-id="element-3443" data-mp="24rcex3uyzkgh">
                {['Families apply with a brief, confidential needs assessment', 'Decisions follow transparent, published criteria', 'Dignity is protected — no family is publicly identified', 'Outcomes are reported quarterly to donors and partners'].map(p => <li key={p} className="flex items-start gap-3" data-id="element-3444" data-mp="3l7oz4yn1pwjy">
                    <CheckCircle2Icon size={18} className="text-forest-700 mt-1 flex-shrink-0" strokeWidth={1.6} data-id="element-3445" data-mp="3l8gk7fj0obvz" />
                    <span data-id="element-3446" data-mp="1bx65fqpgrnmc">{p}</span>
                  </li>)}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-3447" data-mp="1gq8xfuelep0f">
                <Link to="/en/waitlist" data-id="element-3448" data-mp="1krub3hyyb7bo">
                  <Button variant="primary" data-id="element-3449" data-mp="1kqj479a7ljgc">Apply</Button>
                </Link>
                <Link to="/en/partnership/donate-for-children" data-id="element-3450" data-mp="o2d511w23j9g">
                  <Button variant="secondary" data-id="element-3451" data-mp="szv8wdv7v8al">Support a Child</Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-6" data-id="element-3452" data-mp="x0owf64qyl2y">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-card" data-id="element-3453" data-mp="x42va4e7cc5n">
                <img src="https://cdn.magicpatterns.com/uploads/xvXUHwEJEt7AMzu9f6ER5q/Book.jpg" alt="Young Muslim boy writing notes while studying a textbook" className="w-full h-full object-cover" data-id="element-3454" data-mp="11yqb7ev53y8m" />
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>;
}